import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { fmtDateTime, COMPANY_STYLES } from "@/api";
import { useCompany } from "@/store";
import { useAuth } from "@/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Plus, PhoneCall, FileText, Sparkles } from "lucide-react";
import { CustomFieldsForm, CustomFieldsView } from "@/components/CustomFields";

const STATUS_TABS = ["All", "New", "Contacted", "Interested", "Quoted", "Converted", "Lost"];
const OUTCOMES = ["Interested", "Not Interested", "Wrong Number", "Call Back Later"];
const STATUS_STYLE = {
  New: "bg-primary-tint text-primary border-primary/30",
  Contacted: "bg-muted text-muted-foreground border-border",
  Interested: "bg-success-tint text-success border-success/30",
  Quoted: "bg-info-tint text-info border-info/30",
  Converted: "bg-success/15 text-success border-success/40",
  Lost: "bg-muted text-muted-foreground border-border",
};

export default function Leads() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [staff, setStaff] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [tab, setTab] = useState("All");
  const [addOpen, setAddOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ customer_name: "", phone: "", address: "", service: "", company: "Cleanmates", source: "manual", assigned_to: "" });
  const [customForm, setCustomForm] = useState({});
  const [call, setCall] = useState({ outcome: "Interested", notes: "" });
  const [quote, setQuote] = useState({ service: "", amount: "", notes: "" });
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState("");
  const [aiDraft, setAiDraft] = useState("");
  const [slotPref, setSlotPref] = useState("");
  const [slots, setSlots] = useState([]);
  const [pickedSlot, setPickedSlot] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const { company: companyCtx } = useCompany();

  const load = useCallback(() => {
    api.get("/leads", { params: companyCtx !== "all" ? { company: companyCtx } : {} }).then((r) => setLeads(r.data));
    api.get("/companies").then((r) => setCompanies(r.data));
    if (["super_admin", "admin"].includes(user.role)) api.get("/users").then((r) => setStaff(r.data.filter((u) => u.role === "telecaller" && u.active)));
  }, [user.role, companyCtx]);
  useEffect(() => { load(); }, [load]);

  const openDetail = (l) => {
    setAiDraft("");
    api.get(`/leads/${l.id}`).then((r) => setDetail(r.data));
    api.get(`/leads/${l.id}/timeline`).then((r) => setTimeline(r.data.events)).catch(() => setTimeline([]));
  };

  const scoreLead = async () => {
    setAiBusy("score");
    try {
      const r = await api.post(`/ai/lead-score/${detail.id}`);
      setDetail({ ...detail, ...r.data }); load();
      toast.success(`AI Score: ${r.data.ai_score}`);
    } catch { toast.error("AI scoring failed"); }
    setAiBusy("");
  };

  const draftFollowup = async () => {
    setAiBusy("draft");
    try {
      const r = await api.post(`/ai/followup/${detail.id}`);
      setAiDraft(r.data.draft);
    } catch { toast.error("AI draft failed"); }
    setAiBusy("");
  };

  const aiSuggestPrice = async () => {
    setAiBusy("quote");
    try {
      const r = await api.post("/ai/quote-assist", { service: quote.service || detail.service || "General service", company: detail.company });
      setQuote({ ...quote, amount: String(r.data.suggested_price), notes: r.data.description });
      toast.success("AI suggestion applied");
    } catch { toast.error("AI suggestion failed"); }
    setAiBusy("");
  };

  const addLead = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leads", { ...form, assigned_to: form.assigned_to || null, custom_data: customForm });
      toast.success("Lead added");
      setAddOpen(false);
      setForm({ customer_name: "", phone: "", address: "", service: "", company: "Cleanmates", source: "manual", assigned_to: "" });
      setCustomForm({});
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const logCall = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/leads/${detail.id}/calls`, call);
      toast.success("Call logged");
      setCall({ outcome: "Interested", notes: "" });
      openDetail(detail); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const createQuote = async (e) => {
    e.preventDefault();
    try {
      await api.post("/quotations", {
        lead_id: detail.id, company: detail.company, service: quote.service, amount: parseFloat(quote.amount), notes: quote.notes,
        scheduled_date: pickedSlot?.date || null, scheduled_start_time: pickedSlot?.start_time || null,
        duration_minutes: pickedSlot?.duration_minutes || 120, proposed_team_leader_id: pickedSlot?.team_leader_id || null,
      });
      toast.success(pickedSlot ? `Quotation sent — slot ${pickedSlot.date} ${pickedSlot.start_time} proposed` : "Quotation sent for approval");
      setQuoteOpen(false); setQuote({ service: "", amount: "", notes: "" }); setPickedSlot(null); setSlots([]); setSlotPref("");
      openDetail(detail); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const suggestSlots = async () => {
    setAiBusy("slots");
    try {
      const r = await api.get("/schedule/suggest", { params: { company: detail.company, service: quote.service || detail.service, lat: detail.lat ?? undefined, lng: detail.lng ?? undefined, preference: slotPref, duration_minutes: 120 } });
      setSlots(r.data.suggestions);
      if (!r.data.suggestions.length) toast.info("No open slots in the next 14 days");
    } catch { toast.error("Suggestions failed"); }
    setAiBusy("");
  };

  const filtered = tab === "All" ? leads : leads.filter((l) => l.status === tab);
  const summary = {
    total: leads.length,
    new: leads.filter((l) => l.status === "New").length,
    hot: leads.filter((l) => ["Interested", "Quoted", "Converted"].includes(l.status)).length,
    quoted: leads.filter((l) => l.status === "Quoted").length,
  };

  return (
    <div className="space-y-5" data-testid="leads-page">
      <div className="rounded-[28px] border border-border/70 bg-surface-elevated p-5 sm:p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.22em] text-primary">Pipeline</p>
            <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Leads Pipeline</h1>
            <p className="mt-1 text-sm text-muted-foreground">Unified queue — Google Business, Meta Ads, Google Ads & manual</p>
          </div>
          <Button data-testid="add-lead-button" onClick={() => setAddOpen(true)} className="h-11 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-soft hover:bg-primary/90">
            <Plus size={16} className="mr-2" /> Add Lead
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[
          { label: "Total leads", value: summary.total, tone: "bg-info-tint text-info" },
          { label: "New", value: summary.new, tone: "bg-success-tint text-success" },
          { label: "Hot pipeline", value: summary.hot, tone: "bg-warning text-warning-foreground" },
          { label: "Quoted", value: summary.quoted, tone: "bg-primary-tint text-primary" },
        ].map((item) => (
          <Card key={item.label} className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{item.label}</p>
                <div className="mt-3 text-2xl font-bold tabular-nums">{item.value}</div>
              </div>
              <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${item.tone}`}>
                <span className="h-2.5 w-2.5 rounded-full bg-current opacity-80" />
              </span>
            </div>
          </Card>
        ))}
      </div>

      <Card className="p-2 sm:p-3">
        <div className="flex gap-2 flex-wrap" data-testid="lead-status-tabs">
          {STATUS_TABS.map((s) => (
            <button key={s} data-testid={`lead-tab-${s.toLowerCase()}`} onClick={() => setTab(s)}
              className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${tab === s ? "border-primary bg-primary text-primary-foreground shadow-sm" : "border-transparent bg-muted/60 text-foreground hover:bg-muted"}`}>
              {s}
              {s !== "All" && <span className="rounded-full bg-primary-tint/10 px-1.5 py-0.5 text-[10px] opacity-80">{leads.filter((l) => l.status === s).length}</span>}
            </button>
          ))}
        </div>
      </Card>

      <Card className="overflow-hidden">
        <table className="w-full text-sm" data-testid="leads-table">
          <thead className="bg-muted/60 sticky top-0">
            <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              <th className="px-4 py-3">Customer</th><th className="px-4 py-3">Service</th><th className="px-4 py-3">Brand</th>
              <th className="px-4 py-3">Source</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Received</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((l) => (
              <tr key={l.id} data-testid={`lead-row-${l.id}`} onClick={() => openDetail(l)} className="border-t border-border/70 transition-colors hover:bg-accent/40 cursor-pointer">
                <td className="px-4 py-3">
                  <div className="font-medium text-foreground">{l.customer_name}</div>
                  <div className="text-xs text-muted-foreground">{l.phone}</div>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{l.service || "—"}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full border ${COMPANY_STYLES[l.company] || ""}`}>{l.company}</span></td>
                <td className="px-4 py-3"><span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{l.source}</span>{l.escalated && <span className="ml-2 text-[10px] text-danger-foreground font-semibold">SLA!</span>}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 rounded-full border ${STATUS_STYLE[l.status] || ""}`}>{l.status}</span>
                  {l.ai_score && (
                    <span className={`ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${l.ai_score === "Hot" ? "bg-danger text-danger-foreground border-danger" : l.ai_score === "Warm" ? "bg-warning text-warning-foreground border-warning" : "bg-muted text-muted-foreground border-border"}`} data-testid={`ai-score-badge-${l.id}`}>
                      {l.ai_score}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{fmtDateTime(l.created_at)}</td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No leads in this bucket</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-lead-dialog">
          <DialogHeader><DialogTitle className="font-heading">New Lead</DialogTitle></DialogHeader>
          <form onSubmit={addLead} className="space-y-3">
            <Input data-testid="lead-name-input" placeholder="Customer name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} required />
            <Input data-testid="lead-phone-input" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
            <Input data-testid="lead-address-input" placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            <Input data-testid="lead-service-input" placeholder="Service interested in (e.g. 2BHK Deep Cleaning)" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.company} onValueChange={(v) => setForm({ ...form, company: v })}>
                <SelectTrigger data-testid="lead-company-select"><SelectValue /></SelectTrigger>
                <SelectContent>{companies.map((c) => <SelectItem key={c.code} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                <SelectTrigger data-testid="lead-source-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual</SelectItem><SelectItem value="gbp">Google Business</SelectItem>
                  <SelectItem value="meta">Meta Ads</SelectItem><SelectItem value="google">Google Ads</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {staff.length > 0 && (
              <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v })}>
                <SelectTrigger data-testid="lead-assign-select"><SelectValue placeholder="Assign telecaller (optional)" /></SelectTrigger>
                <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <CustomFieldsForm entity="leads" value={customForm} onChange={setCustomForm} />
            <Button data-testid="lead-save-button" type="submit" className="w-full bg-primary hover:bg-primary-hover">Save Lead</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Sheet open={!!detail} onOpenChange={() => setDetail(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="lead-detail-sheet">
          {detail && (
            <>
              <SheetHeader>
                <SheetTitle className="font-heading">{detail.customer_name}</SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-5">
                <div className="text-sm space-y-1.5">
                  <div><span className="text-muted-foreground">Phone:</span> {detail.phone}</div>
                  <div><span className="text-muted-foreground">Address:</span> {detail.address || "—"}</div>
                  <div><span className="text-muted-foreground">Service:</span> {detail.service || "—"}</div>
                  <div className="flex gap-2 pt-1">
                    <span className={`text-xs px-2 py-1 rounded-full border ${COMPANY_STYLES[detail.company] || ""}`}>{detail.company}</span>
                    <span className={`text-xs px-2 py-1 rounded-full border ${STATUS_STYLE[detail.status] || ""}`}>{detail.status}</span>
                  </div>
                </div>
                <CustomFieldsView entity="leads" data={detail.custom_data} />

                {!["Converted", "Lost"].includes(detail.status) && (
                  <Card className="p-4 space-y-3">
                    <div className="font-medium text-sm flex items-center gap-2"><PhoneCall size={15} /> Log Call Outcome</div>
                    <form onSubmit={logCall} className="space-y-3">
                      <div className="grid grid-cols-2 gap-2">
                        {OUTCOMES.map((o) => (
                          <button type="button" key={o} data-testid={`call-outcome-${o.toLowerCase().replace(/ /g, "-")}`}
                            onClick={() => setCall({ ...call, outcome: o })}
                            className={`text-xs px-2 py-2.5 rounded-md border transition-colors ${call.outcome === o ? "bg-primary text-inverse border-primary" : "hover:bg-accent"}`}>
                            {o}
                          </button>
                        ))}
                      </div>
                      <Textarea data-testid="call-notes-input" placeholder="Call notes…" value={call.notes} onChange={(e) => setCall({ ...call, notes: e.target.value })} />
                      <Button data-testid="log-call-button" type="submit" size="sm" className="w-full bg-primary hover:bg-primary-hover">Log Call</Button>
                    </form>
                  </Card>
                )}

                {["Interested", "Contacted", "Quoted"].includes(detail.status) && (
                  <Button data-testid="create-quotation-button" onClick={() => { setQuote({ ...quote, service: detail.service }); setQuoteOpen(true); }} variant="outline" className="w-full">
                    <FileText size={15} className="mr-2" /> Create Quotation
                  </Button>
                )}

                <Card className="p-4 space-y-3 border-primary/30 bg-primary-tint" data-testid="ai-tools-card">
                  <div className="font-medium text-sm flex items-center gap-2"><Sparkles size={15} className="text-primary" /> AI Assistant</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {detail.ai_score && (
                      <span data-testid="ai-score-badge" className={`text-xs font-bold px-2 py-1 rounded-full border ${detail.ai_score === "Hot" ? "bg-danger text-danger-foreground border-danger" : detail.ai_score === "Warm" ? "bg-warning text-warning-foreground border-warning" : "bg-muted text-muted-foreground border-border"}`}>
                        {detail.ai_score} Lead
                      </span>
                    )}
                    {detail.ai_reason && <span className="text-xs text-muted-foreground">{detail.ai_reason}</span>}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" data-testid="ai-score-button" onClick={scoreLead} disabled={!!aiBusy} className="flex-1 h-9">
                      {aiBusy === "score" ? "Scoring…" : detail.ai_score ? "Re-score" : "AI Score"}
                    </Button>
                    <Button size="sm" variant="outline" data-testid="ai-followup-button" onClick={draftFollowup} disabled={!!aiBusy} className="flex-1 h-9">
                      {aiBusy === "draft" ? "Drafting…" : "Follow-up Draft"}
                    </Button>
                  </div>
                  {aiDraft && (
                    <div className="text-sm bg-card border rounded-md p-3 whitespace-pre-wrap" data-testid="ai-followup-draft">
                      {aiDraft}
                      <button data-testid="ai-copy-button" onClick={() => { navigator.clipboard.writeText(aiDraft); toast.success("Copied to clipboard"); }}
                        className="block mt-2 text-xs text-primary font-semibold hover:underline">Copy message</button>
                    </div>
                  )}
                </Card>

                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Customer Journey</div>
                  <div className="relative pl-5 space-y-4 before:absolute before:left-[5px] before:top-1 before:bottom-1 before:w-px before:bg-border" data-testid="customer-timeline">
                    {timeline.map((e) => (
                      <div key={`${e.at}-${e.label}`} className="relative">
                        <span className={`absolute -left-5 top-1 w-[11px] h-[11px] rounded-full border-2 border-background ${e.type === "rating" ? "bg-warning" : e.type === "invoice" ? "bg-success" : e.type === "approved" ? "bg-primary" : e.type === "checkpoint" ? "bg-info" : "bg-muted"}`} />
                        <div className="text-sm font-medium">{e.label}</div>
                        <div className="text-xs text-muted-foreground">{fmtDateTime(e.at)}{e.by ? ` · ${e.by}` : ""}</div>
                        {e.detail && <div className="text-xs text-muted-foreground mt-0.5">{e.detail}</div>}
                        {e.photo && <img src={`${process.env.REACT_APP_BACKEND_URL}${e.photo}`} alt="" className="mt-1.5 w-24 h-24 rounded-lg object-cover border" onError={(ev) => { ev.target.style.display = "none"; }} />}
                      </div>
                    ))}
                    {!timeline.length && <div className="text-sm text-muted-foreground">Journey starts here</div>}
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Call History ({detail.call_logs.length})</div>                  <div className="space-y-2">
                    {detail.call_logs.map((c) => (
                      <div key={c.id} className="text-sm border rounded-md p-3">
                        <div className="flex justify-between"><b>{c.outcome}</b><span className="text-xs text-muted-foreground">{fmtDateTime(c.created_at)}</span></div>
                        <div className="text-muted-foreground mt-1">{c.notes}</div>
                        <div className="text-xs text-muted-foreground mt-1">by {c.telecaller_name}</div>
                      </div>
                    ))}
                    {detail.call_logs.length === 0 && <div className="text-sm text-muted-foreground">No calls logged yet</div>}
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Quotations ({detail.quotations.length})</div>
                  {detail.quotations.map((q) => (
                    <div key={q.id} className="text-sm border rounded-md p-3 mb-2 flex justify-between items-center">
                      <div><b className="font-mono">{q.quote_no}</b> — {q.service}</div>
                      <span className={`text-xs px-2 py-1 rounded-full border ${q.status === "Approved" ? "bg-success-tint text-success border-success/30" : q.status === "Rejected" ? "bg-danger-tint text-danger border-danger/30" : "bg-warning-tint text-warning border-warning/30"}`}>{q.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
        <DialogContent data-testid="quotation-dialog">
          <DialogHeader><DialogTitle className="font-heading">New Quotation — {detail?.customer_name}</DialogTitle></DialogHeader>
          <form onSubmit={createQuote} className="space-y-3">
            <div className="text-sm text-muted-foreground">Brand: <b className="text-foreground">{detail?.company}</b></div>
            <Button type="button" variant="outline" size="sm" data-testid="quote-ai-suggest-button" onClick={aiSuggestPrice} disabled={!!aiBusy} className="w-full">
              <Sparkles size={14} className="mr-2" /> {aiBusy === "quote" ? "AI is thinking…" : "AI Suggest Price & Description"}
            </Button>
            <div className="border rounded-md p-3 space-y-2 bg-muted/30" data-testid="quote-slot-panel">
              <div className="flex items-center justify-between gap-2">
                <label className="text-sm font-medium">Propose a slot while on the call</label>
                {pickedSlot && <span className="text-xs text-success font-semibold" data-testid="picked-slot">{pickedSlot.date} {pickedSlot.start_time} · {pickedSlot.team_leader_name}</span>}
              </div>
              <div className="flex gap-2">
                <Input data-testid="slot-preference-input" placeholder='Customer preference, e.g. "mornings" or "weekend"' value={slotPref} onChange={(e) => setSlotPref(e.target.value)} className="h-9 text-sm" />
                <Button type="button" variant="outline" size="sm" data-testid="slot-suggest-button" onClick={suggestSlots} disabled={!!aiBusy} className="h-9 shrink-0">
                  <Sparkles size={13} className="mr-1" /> {aiBusy === "slots" ? "Thinking…" : "AI Slots"}
                </Button>
              </div>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {slots.map((s, i) => (
                  <button type="button" key={`${s.date}-${s.start_time}-${s.team_leader_id}`} data-testid={`slot-option-${i}`} onClick={() => setPickedSlot(s)}
                    className={`w-full text-left text-xs border rounded-md px-3 py-2 transition-colors ${pickedSlot === s ? "border-primary bg-primary-tint" : "hover:bg-accent"}`}>
                    <b>{s.date}</b> · {s.start_time}–{s.end_time} · {s.team_leader_name}
                    <div className="text-muted-foreground">{s.reason}</div>
                  </button>
                ))}
              </div>
            </div>
            <Input data-testid="quote-service-input" placeholder="Service (e.g. 3BHK Deep Cleaning)" value={quote.service} onChange={(e) => setQuote({ ...quote, service: e.target.value })} required />
            <Input data-testid="quote-amount-input" type="number" min="1" step="0.01" placeholder="Amount (₹)" value={quote.amount} onChange={(e) => setQuote({ ...quote, amount: e.target.value })} required />
            <Textarea data-testid="quote-notes-input" placeholder="Notes (optional)" value={quote.notes} onChange={(e) => setQuote({ ...quote, notes: e.target.value })} />
            <Button data-testid="quote-submit-button" type="submit" className="w-full bg-primary hover:bg-primary-hover">Send for Approval</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
